import {
  lineItemResolutionSchema,
  type LineItemResolutionFormValues,
} from "@babackapp/return-widget-components/forms/line-item-resolution.form";
import { useAvailableResolutions } from "@babackapp/return-widget-components/hooks/use-available-resolutions";
import {
  getAutoCommittableLeaf,
  type SingleFamilyLeaf,
} from "@babackapp/return-widget-components/lib/resolutions.lib";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { useReturnReasons } from "@babackapp/return-widget-components/hooks/use-return-reasons";
import { useUpdateLineItem } from "@babackapp/return-widget-components/hooks/use-update-line-item";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import type { OrderLineItem, Resolution } from "@babackapp/return-widget-components/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLayoutEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useCurrentOrderLineItems } from "./order";
import { useWidgetParams } from "./use-widget-params";

type TopLevelResolution = "refund" | "exchange";

function toTopLevel(resolutions: Resolution[]): TopLevelResolution[] {
  const result: TopLevelResolution[] = [];
  if (resolutions.some((r) => r === "exchange" || r === "exchange-credit"))
    result.push("exchange");
  if (resolutions.some((r) => r === "refund" || r === "discount"))
    result.push("refund");
  return result;
}

function draftToTopLevel(
  resolution: string | null | undefined,
): TopLevelResolution | null {
  if (resolution === "exchange" || resolution === "exchange-credit")
    return "exchange";
  if (resolution === "refund" || resolution === "discount") return "refund";
  return null;
}

export function useLineItemResolutionForm() {
  const navigate = useNavigate();
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const { orderId, shopId } = useWidgetParams();
  const { data: shop } = usePublicShop(shopId);
  const updateLineItem = useUpdateLineItem(orderId);
  const draft = useDraftReturn(orderId);
  const draftLineItem = draft?.lineItems.find(
    (li) => li.lineItemId === lineItemId,
  );
  const { data: lineItems = [] } = useCurrentOrderLineItems();
  const { data: order } = usePublicOrder(orderId);
  const reasons = useReturnReasons(shopId);
  const selectedReason = reasons.find((r) => r.id === draftLineItem?.reasonId) ?? null;
  const isRetractationItem = selectedReason?.isRetractation ?? false;
  const shippingAmount = order?.financialInformations?.return_shipping_paid?.presentment_amount ?? 0;
  const shippingCurrency = order?.financialInformations?.refund?.currency ?? "EUR";
  const formattedShippingCost = formatPrice(shippingAmount, shippingCurrency);

  const lineItem = lineItems.find((li) => li.line_item_id === lineItemId);
  // variantByLineItemId values are untyped in the generated schema (Record<string, unknown>)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const variantCollection = (order?.variantByLineItemId?.[lineItemId] ?? null) as any;

  const rawResolutions = useAvailableResolutions(
    shopId,
    lineItem ?? ({} as OrderLineItem),
    variantCollection,
  );

  const topLevelResolutions = toTopLevel(rawResolutions);
  const savedTopLevel = draftToTopLevel(draftLineItem?.resolution);
  const defaultResolution = savedTopLevel ?? topLevelResolutions[0] ?? "refund";

  const form = useForm<LineItemResolutionFormValues>({
    resolver: zodResolver(lineItemResolutionSchema),
    defaultValues: {
      resolution: defaultResolution,
      resolutionOption: null,
      exchangeVariantId: null,
    },
  });

  // A single sub-option needs no further choice — commit it and go to summary.
  const commitLeaf = (
    leaf: NonNullable<SingleFamilyLeaf>,
    options?: { replace?: boolean },
  ) => {
    updateLineItem(lineItemId, {
      resolution: leaf.resolution,
      resolutionOption: leaf.resolutionOption,
    });
    navigate("../../../summary", { replace: options?.replace ?? false });
  };

  const goToFamily = (family: TopLevelResolution) => {
    updateLineItem(lineItemId, { resolution: family });
    navigate("../resolution-options", { replace: true });
  };

  useLayoutEffect(() => {
    if (topLevelResolutions.length !== 1) return;
    const family = topLevelResolutions[0];
    const autoLeaf = getAutoCommittableLeaf(family, rawResolutions);
    if (autoLeaf) return commitLeaf(autoLeaf, { replace: true });
    goToFamily(family);
  }, [topLevelResolutions.length, lineItemId, navigate, updateLineItem]);

  const onSubmit = form.handleSubmit((values) => {
    const autoLeaf = getAutoCommittableLeaf(values.resolution, rawResolutions);
    if (autoLeaf) return commitLeaf(autoLeaf);
    const currentTopLevel = draftToTopLevel(draftLineItem?.resolution);
    if (values.resolution !== currentTopLevel) {
      updateLineItem(lineItemId, { resolution: values.resolution });
    }
    navigate("../resolution-options");
  });

  return {
    form,
    onSubmit,
    commitLeaf,
    rawResolutions,
    lineItemId,
    availableResolutions: topLevelResolutions,
    isRetractationItem,
    formattedShippingCost,
    userPortalTexts: shop?.preferences?.userPortal?.resolutionHeaderInfo,
  };
}
